import type { Event } from '../types';

import { findAdjacentSlot } from './columnSlots';

interface NoOpDropArgs {
  /** The route the card came from. */
  sourcePlanningEntityId: string;
  /** The route the drop resolved to. */
  targetPlanningEntityId?: string;
  /** The moment the drop would plan the card at. */
  targetStart?: string;
  /**
   * The card's own schedule, in render order, so the slots either side of it can
   * be identified. Adjacency only means anything within one schedule, so this is
   * that schedule's events and not the column's flattened list. Omitted by a
   * view that has no slots around its cards (the roster), which then falls back
   * to the moment comparison alone.
   */
  scheduleEvents?: Event[];
  /**
   * Whether the release was on a card's body rather than on the slot itself.
   * Cards delegate to the slot beside them, so the two arrive here as the same
   * request, and only this tells them apart.
   */
  viaCardBody?: boolean;
  /** The card being dragged. */
  event: Event;
  /** How many cards this drag carries. */
  draggedCount: number;
}

/**
 * Whether a drop would leave the card exactly where it already is.
 *
 * Three ways that happens, and the reason it takes more than comparing moments:
 * a slot carries the moment the drop plans at, and for most cards that moment is
 * neither the card's start nor its end.
 *
 * 1. The slot carries the card's own start. That is the synthetic DROPZONE above
 *    it — `addDropzones` gives that one the previous appointment's `end`, which
 *    for two cards back to back is this card's start.
 *
 * 2. The slot is next to the card in its own route and is not free time: the
 *    DROPZONE below it, or the travel time on either side. Travel is not a
 *    destination of its own — the inbound travel is how the card is reached and
 *    the outbound is how the next one is, so planning the card "into" either
 *    puts it back in the position it already holds, however different the
 *    slot's own start looks. This is the common case on a real route, where
 *    travel time sits between every pair of appointments: without it, picking a
 *    card up and putting it straight back down asked the planner to choose a
 *    recurrence scope for a move that moves nothing.
 *
 * 3. Free time next to the card, when the release was on the card's body. Free
 *    time *is* a real destination — dropping a card into the gap above or below
 *    it moves it there, and that gesture has to keep working — but a card
 *    delegates its own halves to those slots, so releasing on the card itself
 *    would otherwise plan it into the gap. Released on the card, it stays; only
 *    a release on the gap moves it there.
 *
 * The route has to match throughout: another route can easily have a slot
 * starting at the same clock time, and moving there is a real move.
 *
 * Only for a single-card drag. Dropping a multi-selection next to the card that
 * was grabbed still gathers the others, so it is not a no-op for them.
 */
export const isNoOpDrop = ({
  sourcePlanningEntityId,
  targetPlanningEntityId,
  targetStart,
  scheduleEvents,
  viaCardBody,
  event,
  draggedCount
}: NoOpDropArgs): boolean => {
  if (draggedCount > 1) return false;
  if (!targetStart || targetPlanningEntityId !== sourcePlanningEntityId) {
    return false;
  }
  if (targetStart === event.start) return true;

  const events = scheduleEvents ?? [];
  const index = events.findIndex(({ id }) => id === event.id);
  if (index < 0) return false;

  // The same two slots the card delegates its halves to, so a release on the
  // card and a release on the slot it points at agree on the answer.
  const slot = [
    findAdjacentSlot(events, index, -1),
    findAdjacentSlot(events, index, 1)
  ].find((adjacent) => adjacent?.start === targetStart);

  if (!slot) return false;
  return !!viaCardBody || slot.category !== 'FREE';
};
